import React, { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  FolderIcon,
  HomeIcon,
  ArrowRightIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { Button, Spinner } from "components/ui";
import clsx from "clsx";
import { toast } from "sonner";
import { moveDocumentItem, moveDocumentItems, getDocumentFolders, DOCUMENT_ENTITY_TYPES } from "services/documentFolderService";
import { ConfirmModal } from "components/shared/ConfirmModal";

const MoveItemModal = ({
  isOpen,
  onClose,
  itemToMove = null,
  itemsToMove = [],
  entityType = DOCUMENT_ENTITY_TYPES.LEAD,
  entityId = null,
  rootFolderId = null,
  currentFolderId = null,
  onSuccess,
}) => {
  // Navigation state inside the modal
  const [navFolderId, setNavFolderId] = useState(null);
  const [navFolderPath, setNavFolderPath] = useState([{ id: null, name: "Root" }]);
  const [navSubfolders, setNavSubfolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);

  // Resolved root folder ID from backend
  const [actualRootId, setActualRootId] = useState(rootFolderId);

  // Target selected destination ID
  const [selectedDestinationId, setSelectedDestinationId] = useState(rootFolderId || null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Confirm modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Normalize items array
  const effectiveItemsToMove = useMemo(() => {
    if (itemsToMove && Array.isArray(itemsToMove) && itemsToMove.length > 0) {
      return itemsToMove;
    }
    if (itemToMove) {
      return [itemToMove];
    }
    return [];
  }, [itemToMove, itemsToMove]);

  // Calculate item breakdown (folders vs files)
  const { folderCount, fileCount } = useMemo(() => {
    let folders = 0;
    let files = 0;
    effectiveItemsToMove.forEach((item) => {
      const isFolder =
        item?.isFolderItem !== undefined
          ? Boolean(item.isFolderItem)
          : Boolean(item?.FolderId && !item?.DocumentId && !item?.filePath && !item?.FilePath);
      if (isFolder) folders++;
      else files++;
    });
    return { folderCount: folders, fileCount: files };
  }, [effectiveItemsToMove]);

  const itemCount = effectiveItemsToMove.length;
  const isMultiple = itemCount > 1;

  const firstItemName = effectiveItemsToMove[0]?.Name || effectiveItemsToMove[0]?.name || "Item";

  // Build specific item title (e.g. '3 Folders', '2 Files', '2 Folders & 1 File', or '"Filename.pdf"')
  const itemSummaryTitle = useMemo(() => {
    if (itemCount === 0) return "Items";
    if (itemCount === 1) {
      return `"${firstItemName}"`;
    }
    if (folderCount > 0 && fileCount === 0) {
      return `${folderCount} Folders`;
    }
    if (fileCount > 0 && folderCount === 0) {
      return `${fileCount} Files`;
    }
    return `${folderCount} ${folderCount === 1 ? "Folder" : "Folders"} & ${fileCount} ${fileCount === 1 ? "File" : "Files"}`;
  }, [itemCount, folderCount, fileCount, firstItemName]);

  // Build specific item description string (e.g. '3 folders', '2 files', '2 folders and 1 file', or '"Filename.pdf"')
  const itemSummaryDescription = useMemo(() => {
    if (itemCount === 0) return "items";
    if (itemCount === 1) {
      return `"${firstItemName}"`;
    }
    if (folderCount > 0 && fileCount === 0) {
      return `${folderCount} folders`;
    }
    if (fileCount > 0 && folderCount === 0) {
      return `${fileCount} files`;
    }
    return `${folderCount} ${folderCount === 1 ? "folder" : "folders"} and ${fileCount} ${fileCount === 1 ? "file" : "files"}`;
  }, [itemCount, folderCount, fileCount, firstItemName]);

  // Collect folder IDs being moved to exclude from destination choices
  const movingFolderIds = useMemo(() => {
    return new Set(
      effectiveItemsToMove
        .filter((item) => {
          if (item?.isFolderItem !== undefined) return Boolean(item.isFolderItem);
          return Boolean(item?.FolderId && !item?.DocumentId && !item?.filePath && !item?.FilePath);
        })
        .map((item) => String(item?.FolderId || item?.id))
    );
  }, [effectiveItemsToMove]);


  // Fetch subfolders for current navFolderId
  const fetchSubfolders = useCallback(
    async (targetFolderId) => {
      setLoadingFolders(true);
      try {
        const res = await getDocumentFolders({
          entityType,
          entityId,
          folderId: targetFolderId,
          pageSize: 100,
        });

        if (res.success) {
          setNavSubfolders(res.folders || []);
          if (res.currentFolderId) {
            setActualRootId((prev) => prev || res.currentFolderId);
            if (!targetFolderId) {
              setSelectedDestinationId((prev) => prev || res.currentFolderId);
            }
          }
        } else {
          setNavSubfolders([]);
        }
      } catch (err) {
        console.error("Error fetching subfolders in MoveItemModal:", err);
        setNavSubfolders([]);
      } finally {
        setLoadingFolders(false);
      }
    },
    [entityType, entityId]
  );

  // Initialize and load subfolders when modal opens
  useEffect(() => {
    if (isOpen) {
      setNavFolderId(null);
      setNavFolderPath([{ id: null, name: "Root" }]);
      setSelectedDestinationId(rootFolderId || null);
      setShowConfirmModal(false);
      fetchSubfolders(null);
    }
  }, [isOpen, rootFolderId, fetchSubfolders]);

  // Navigate deeper into a subfolder
  const handleNavigateToSubfolder = (folderId, folderName) => {
    setNavFolderId(folderId);
    setSelectedDestinationId(folderId);
    setNavFolderPath((prev) => [...prev, { id: folderId, name: folderName }]);
    fetchSubfolders(folderId);
  };

  // Jump to a breadcrumb item
  const handleBreadcrumbClick = (folderId, index) => {
    setNavFolderId(folderId);
    const targetDest = folderId || actualRootId || rootFolderId;
    setSelectedDestinationId(targetDest);
    setNavFolderPath((prev) => prev.slice(0, index + 1));
    fetchSubfolders(folderId);
  };

  // Exclude folders being moved (and their children) from target choices
  const availableFolders = navSubfolders.filter((f) => {
    const fId = String(f.FolderId || f.id);
    if (movingFolderIds.has(fId)) return false;
    return true;
  });

  // Calculate destination folder name for display in ConfirmModal
  const destinationFolderName = useMemo(() => {
    const resolvedRoot = actualRootId || rootFolderId;
    const targetId = selectedDestinationId || navFolderId || resolvedRoot;
    if (!targetId || targetId === resolvedRoot) {
      return navFolderPath[0]?.name || "Root";
    }
    const foundInNav = navFolderPath.find((item) => String(item.id) === String(targetId));
    if (foundInNav) return foundInNav.name;
    const foundInSub = navSubfolders.find((f) => String(f.FolderId || f.id) === String(targetId));
    if (foundInSub) return foundInSub.Name || foundInSub.name;
    return navFolderPath[navFolderPath.length - 1]?.name || "Selected Folder";
  }, [selectedDestinationId, navFolderId, actualRootId, rootFolderId, navFolderPath, navSubfolders]);

  // Check if target destination is the current folder of ALL items being moved
  const isTargetSameAsCurrent = useMemo(() => {
    if (!effectiveItemsToMove || effectiveItemsToMove.length === 0) return false;
    const resolvedRoot = actualRootId || rootFolderId;
    const targetId = String(selectedDestinationId || navFolderId || resolvedRoot || "");
    if (!targetId) return false;

    return effectiveItemsToMove.every((item) => {
      const parentId = !item.isFolderItem
        ? (item.FolderId || item.folderId)
        : (item.ParentFolderId || item.parentFolderId);
      const resolvedParent =
        parentId !== null && parentId !== undefined
          ? String(parentId)
          : String(currentFolderId || resolvedRoot || "");
      return resolvedParent === targetId;
    });
  }, [selectedDestinationId, navFolderId, actualRootId, rootFolderId, effectiveItemsToMove, currentFolderId]);

  const handleInitiateMove = () => {
    if (effectiveItemsToMove.length === 0) {
      toast.error("No items selected for move.");
      return;
    }

    const resolvedRoot = actualRootId || rootFolderId;
    const targetDestination =
      selectedDestinationId || navFolderId || resolvedRoot;

    if (!targetDestination) {
      toast.error("Unable to resolve destination folder ID.");
      return;
    }

    if (isTargetSameAsCurrent) {
      toast.error("Selected items are already in this folder. Please choose a different destination folder.");
      return;
    }

    setShowConfirmModal(true);
  };

  const handleExecuteMove = async () => {
    const resolvedRoot = actualRootId || rootFolderId;
    const targetDestination =
      selectedDestinationId || navFolderId || resolvedRoot;

    const ids = effectiveItemsToMove.map((item) => {
      const isFolder =
        item?.isFolderItem !== undefined
          ? Boolean(item.isFolderItem)
          : Boolean(
              item?.FolderId &&
                !item?.DocumentId &&
                !item?.filePath &&
                !item?.FilePath
            );
      return isFolder
        ? item?.FolderId || item?.id
        : item?.DocumentId || item?.id;
    }).filter(Boolean);

    if (ids.length === 0) {
      toast.error("No valid item IDs found to move.");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await moveDocumentItems({
        ids,
        destinationFolderId: targetDestination,
      });

      if (res.success) {
        toast.success(res.message || `Moved ${itemSummaryDescription} successfully.`);
        setShowConfirmModal(false);
        if (onSuccess) await onSuccess();
        onClose();
      } else {
        toast.error(res.error || "Failed to move items.");
      }
    } catch (err) {
      console.error("Error in handleExecuteMove:", err);
      toast.error("An error occurred while moving items.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const effectiveCurrentTarget = navFolderId || actualRootId || rootFolderId;
  const isCurrentOpenFolderSelected =
    selectedDestinationId === effectiveCurrentTarget ||
    (!selectedDestinationId && navFolderId === null);

  return (
    <>
      <Transition appear show={Boolean(isOpen)} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-xs" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-lg transform overflow-hidden rounded-xl bg-white dark:bg-dark-800 p-6 text-left align-middle shadow-xl transition-all border border-gray-200 dark:border-dark-600">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-dark-700">
                    <DialogTitle
                      as="h3"
                      className="text-base font-semibold text-gray-900 dark:text-dark-50 flex items-center gap-2 truncate"
                    >
                      <ArrowRightIcon className="h-5 w-5 text-primary-600 shrink-0" />
                      <span className="truncate">Move {itemSummaryTitle}</span>
                    </DialogTitle>
                    <button
                      type="button"
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-200 p-1 rounded-md"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Sub-header & Breadcrumb Navigation */}
                  <div className="mt-4">
                    <p className="text-xs text-gray-500 dark:text-dark-300 mb-2">
                      Select target folder location:
                    </p>

                    {/* Modal Folder Breadcrumbs */}
                    <div className="flex items-center flex-wrap gap-1 p-2 bg-gray-50 dark:bg-dark-700/50 rounded-lg border border-gray-200 dark:border-dark-600 text-xs mb-3">
                      {navFolderPath.map((item, index) => (
                        <React.Fragment key={item.id || `root-${index}`}>
                          {index > 0 && (
                            <ChevronRightIcon className="h-3 w-3 text-gray-400 shrink-0" />
                          )}
                          <button
                            type="button"
                            onClick={() => handleBreadcrumbClick(item.id, index)}
                            className={clsx(
                              "flex items-center gap-1 font-medium hover:text-primary-600 dark:hover:text-primary-400 transition-colors",
                              index === navFolderPath.length - 1
                                ? "text-primary-600 dark:text-primary-400 font-semibold"
                                : "text-gray-600 dark:text-dark-300"
                            )}
                          >
                            {index === 0 && <HomeIcon className="h-3.5 w-3.5" />}
                            <span>{item.name}</span>
                          </button>
                        </React.Fragment>
                      ))}
                    </div>

                    {/* Folders List Container */}
                    <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1 min-h-[160px]">
                      {/* Current Open Folder as Destination Target */}
                      <div
                        onClick={() => setSelectedDestinationId(effectiveCurrentTarget)}
                        className={clsx(
                          "flex items-center justify-between p-2.5 rounded-lg border text-xs cursor-pointer transition-all",
                          isCurrentOpenFolderSelected
                            ? "border-primary-500 bg-primary-50/70 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold shadow-2xs"
                            : "border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 text-gray-700 dark:text-dark-200"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <HomeIcon className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
                          <span>Move to this folder ({navFolderPath[navFolderPath.length - 1]?.name})</span>
                        </div>
                        {isCurrentOpenFolderSelected && (
                          <span
                            className={clsx(
                              "text-[10px] px-2 py-0.5 rounded-full font-medium",
                              isTargetSameAsCurrent
                                ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700"
                                : "bg-primary-600 text-white"
                            )}
                          >
                            {isTargetSameAsCurrent ? "Current Location" : "Selected"}
                          </span>
                        )}
                      </div>

                      {/* Subfolder Items */}
                      {loadingFolders ? (
                        <div className="flex items-center justify-center p-8 text-gray-400">
                          <Spinner className="h-5 w-5 text-primary-500 animate-spin" />
                        </div>
                      ) : availableFolders.length === 0 ? (
                        <div className="p-4 text-center text-xs text-gray-400 italic">
                          No subfolders in this location
                        </div>
                      ) : (
                        availableFolders.map((f) => {
                          const fId = f.FolderId || f.id;
                          const isSelected = selectedDestinationId === fId;

                          return (
                            <div
                              key={fId}
                              onClick={() => setSelectedDestinationId(fId)}
                              className={clsx(
                                "flex items-center justify-between p-2.5 rounded-lg border text-xs cursor-pointer transition-all group",
                                isSelected
                                  ? "border-primary-500 bg-primary-50/70 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold shadow-2xs"
                                  : "border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 text-gray-700 dark:text-dark-200"
                              )}
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1 pr-2">
                                <FolderIcon className="h-4 w-4 shrink-0 text-amber-500" />
                                <span className="truncate">{f.Name || f.name}</span>
                              </div>

                              <div className="flex items-center gap-2 shrink-0">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavigateToSubfolder(fId, f.Name || f.name);
                                  }}
                                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-gray-600 dark:text-dark-200 hover:text-primary-600 dark:hover:text-primary-400 hover:border-primary-300 transition-colors"
                                >
                                  <span>Open</span>
                                  <ChevronRightIcon className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Footer Buttons */}
                  <div className="mt-6 flex items-center justify-end gap-3 pt-3 border-t border-gray-100 dark:border-dark-700">
                    <Button
                      type="button"
                      variant="outlined"
                      color="secondary"
                      onClick={onClose}
                      disabled={isSubmitting}
                      className="text-xs"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      color="primary"
                      onClick={handleInitiateMove}
                      disabled={isSubmitting || isTargetSameAsCurrent}
                      className="text-xs font-semibold px-4 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      title={isTargetSameAsCurrent ? "Selected items are already in this folder" : "Move items here"}
                    >
                      Move Here
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Confirm Modal before executing move */}
      <ConfirmModal
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onOk={handleExecuteMove}
        confirmLoading={isSubmitting}
        state="pending"
        buttonColor="primary"
        messages={{
          pending: {
            title: `Move ${itemSummaryTitle}`,
            description: `Are you sure you want to move ${itemSummaryDescription} to "${destinationFolderName}"?`,
            actionText: "Move",
          },
          success: {
            title: "Items Moved",
            description: `Selected ${itemSummaryDescription} have been successfully moved.`,
            actionText: "Done",
          },
          error: {
            title: "Move Failed",
            description: "Failed to move the items. Please try again.",
            actionText: "Retry",
          },
        }}
      />
    </>
  );
};

export default MoveItemModal;
